import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  TrendingUp, 
  DollarSign, 
  Lock, 
  Unlock,
  Activity,
  AlertCircle,
  Coins,
  Wallet,
  ArrowLeft,
  Shield,
  CreditCard,
  CheckCircle,
  Info,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DeFi = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [position, setPosition] = useState(null);
  const [stakingInfo, setStakingInfo] = useState(null);
  const [bethBalance, setBethBalance] = useState(0);
  const [protocolStats, setProtocolStats] = useState(null);
  const [ethPrice, setEthPrice] = useState(3500);
  
  // Form states
  const [collateralAmount, setCollateralAmount] = useState('');
  const [borrowAmount, setBorrowAmount] = useState('');
  const [repayAmount, setRepayAmount] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    fetchDeFiData();
    const interval = setInterval(fetchDeFiData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDeFiData = async () => {
    try {
      const [positionRes, stakingRes, bethRes, statsRes, priceRes] = await Promise.all([
        axios.get(`${API}/defi/position`, { headers: getAuthHeaders() }),
        axios.get(`${API}/defi/staking-info`, { headers: getAuthHeaders() }),
        axios.get(`${API}/defi/beth-balance`, { headers: getAuthHeaders() }),
        axios.get(`${API}/defi/protocol-stats`),
        axios.get(`${API}/defi/eth-price`)
      ]);

      setPosition(positionRes.data);
      setStakingInfo(stakingRes.data);
      setBethBalance(bethRes.data.balance || 0);
      setProtocolStats(statsRes.data);
      setEthPrice(priceRes.data.price_usd || 3500);
    } catch (error) {
      console.error('Error fetching DeFi data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeposit = async () => {
    if (!collateralAmount || parseFloat(collateralAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await axios.post(`${API}/defi/deposit`, 
        { amount: parseFloat(collateralAmount), collateral_type: 'BETH' },
        { headers: getAuthHeaders() }
      );
      toast.success('Collateral deposited successfully');
      setCollateralAmount('');
      fetchDeFiData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Deposit failed');
    }
  };

  const handleBorrow = async () => {
    if (!borrowAmount || parseFloat(borrowAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await axios.post(`${API}/defi/borrow`,
        { amount: parseFloat(borrowAmount) },
        { headers: getAuthHeaders() }
      );
      toast.success('Credit drawn successfully');
      setBorrowAmount('');
      fetchDeFiData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Borrow failed');
    }
  };

  const handleRepay = async () => {
    if (!repayAmount || parseFloat(repayAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    try {
      await axios.post(`${API}/defi/repay`,
        { amount: parseFloat(repayAmount) },
        { headers: getAuthHeaders() }
      );
      toast.success('Repayment successful');
      setRepayAmount('');
      fetchDeFiData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Repayment failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#b8860b]"></div>
      </div>
    );
  }

  const healthColor = position?.health_factor >= 150 ? 'text-green-400' : 
                     position?.health_factor >= 120 ? 'text-yellow-400' : 'text-red-400';

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="credit-facility">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
            </Link>
            <div className="h-6 w-px bg-[#1a2d4a]" />
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#b8860b]" />
                Credit Facility
              </h1>
              <p className="text-xs text-gray-500">Collateral Management & Credit Access</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchDeFiData} className="border-[#1a2d4a]">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Key Metrics */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <Wallet className="w-4 h-4 text-[#b8860b]" />
                <span className="text-xs text-gray-500">Total Collateral</span>
              </div>
              <p className="text-2xl font-bold text-white">
                ${position?.total_collateral_usd?.toFixed(2) || '0.00'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {position?.collateral_amount?.toFixed(2) || '0'} BETH @ $1.00
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-gray-500">Credit Used</span>
              </div>
              <p className="text-2xl font-bold text-white">
                ${position?.debt_amount?.toFixed(2) || '0.00'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Max: ${position?.max_borrow?.toFixed(2) || '0.00'}
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-purple-400" />
                <span className="text-xs text-gray-500">Health Factor</span>
              </div>
              <p className={`text-2xl font-bold ${healthColor}`}>
                {position?.health_factor ? (position.health_factor / 100).toFixed(2) : '∞'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Liquidation at 1.20
              </p>
            </CardContent>
          </Card>

          <Card className="bg-[#0d1d35] border-[#1a2d4a]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-2">
                <Coins className="w-4 h-4 text-[#b8860b]" />
                <span className="text-xs text-gray-500">BETH Balance</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {bethBalance.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Available to pledge
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Get BETH Banner - Show if balance is low */}
        {bethBalance < 100 && (
          <Card className="bg-[#b8860b]/10 border-[#b8860b]/30 mb-6">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Coins className="w-6 h-6 text-[#b8860b]" />
                  <div>
                    <p className="text-white font-medium">Need BETH to pledge as collateral?</p>
                    <p className="text-sm text-gray-400">Acquire BETH tokens to access credit facilities</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    className="bg-[#b8860b] hover:bg-[#9a7209]"
                    onClick={() => navigate('/subscribe')}
                  >
                    Get BETH Credits
                  </Button>
                  <Button 
                    variant="outline" 
                    className="border-[#b8860b] text-[#b8860b]"
                    onClick={() => navigate('/credit-enhancement')}
                  >
                    Platform Tools
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs defaultValue="collateral" className="space-y-6">
          <TabsList className="bg-[#0d1d35] border border-[#1a2d4a] p-1">
            <TabsTrigger value="collateral" className="data-[state=active]:bg-[#b8860b] data-[state=active]:text-white">
              <Lock className="w-4 h-4 mr-2" />
              Collateral
            </TabsTrigger>
            <TabsTrigger value="credit" className="data-[state=active]:bg-[#b8860b] data-[state=active]:text-white">
              <CreditCard className="w-4 h-4 mr-2" />
              Draw Credit
            </TabsTrigger>
            <TabsTrigger value="repay" className="data-[state=active]:bg-[#b8860b] data-[state=active]:text-white">
              <DollarSign className="w-4 h-4 mr-2" />
              Repay
            </TabsTrigger>
          </TabsList>

          {/* Collateral Tab */}
          <TabsContent value="collateral">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[#b8860b]" />
                    Pledge Collateral
                  </CardTitle>
                  <CardDescription>Deposit BETH tokens as collateral to access credit</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Amount (BETH)</label>
                    <Input
                      type="number"
                      placeholder="Enter amount"
                      value={collateralAmount}
                      onChange={(e) => setCollateralAmount(e.target.value)}
                      className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Available: {bethBalance.toFixed(2)} BETH
                    </p>
                  </div>
                  <Button 
                    className="w-full bg-[#b8860b] hover:bg-[#9a7209]"
                    onClick={handleDeposit}
                  >
                    <Lock className="w-4 h-4 mr-2" />
                    Pledge Collateral
                  </Button>
                  
                  {bethBalance < 100 && (
                    <div className="pt-3 border-t border-[#1a2d4a] mt-3">
                      <p className="text-xs text-gray-500 mb-2">Need more BETH credits?</p>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="flex-1 border-[#b8860b]/50 text-[#b8860b] hover:bg-[#b8860b]/10"
                          onClick={() => navigate('/subscribe')}
                        >
                          Get BETH Credits
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-[#1a2d4a]"
                          onClick={() => navigate('/credit-enhancement')}
                        >
                          Tools
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-400" />
                    Collateral Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-[#1a2d4a]/50 rounded-lg space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Pledged BETH</span>
                      <span className="text-white font-medium">{position?.collateral_amount?.toFixed(2) || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">USD Value</span>
                      <span className="text-white font-medium">${position?.total_collateral_usd?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">LTV Ratio</span>
                      <span className="text-white font-medium">75% Max</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Credit Available</span>
                      <span className="text-green-400 font-medium">${position?.max_borrow?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Credit Tab */}
          <TabsContent value="credit">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-[#b8860b]" />
                    Draw Credit
                  </CardTitle>
                  <CardDescription>Borrow against your pledged collateral</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Amount (USD)</label>
                    <Input
                      type="number"
                      placeholder="Enter amount"
                      value={borrowAmount}
                      onChange={(e) => setBorrowAmount(e.target.value)}
                      className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Max available: ${position?.max_borrow?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <Button 
                    className="w-full bg-[#b8860b] hover:bg-[#9a7209]"
                    onClick={handleBorrow}
                    disabled={!position?.max_borrow || position.max_borrow <= 0}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Draw Credit
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-400" />
                    Credit Terms
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">6-10% APR based on utilization</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">No fixed repayment schedule</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Interest accrues daily</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Liquidation at 120% health factor</span>
                    </div>
                  </div>
                  
                  {position?.debt_amount > 0 && (
                    <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg">
                      <p className="text-sm text-yellow-400 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Current debt: ${position.debt_amount.toFixed(2)}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Repay Tab */}
          <TabsContent value="repay">
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-400" />
                    Repay Credit
                  </CardTitle>
                  <CardDescription>Reduce your outstanding credit balance</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm text-gray-400 mb-2 block">Amount (USD)</label>
                    <Input
                      type="number"
                      placeholder="Enter amount"
                      value={repayAmount}
                      onChange={(e) => setRepayAmount(e.target.value)}
                      className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Outstanding: ${position?.debt_amount?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      className="flex-1 border-[#1a2d4a]"
                      onClick={() => setRepayAmount((position?.debt_amount || 0).toFixed(2))}
                    >
                      Repay All
                    </Button>
                    <Button 
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={handleRepay}
                      disabled={!position?.debt_amount || position.debt_amount <= 0}
                    >
                      Repay
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#0d1d35] border-[#1a2d4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Unlock className="w-5 h-5 text-[#b8860b]" />
                    After Repayment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 text-sm mb-4">
                    After repaying your credit balance, you can withdraw your pledged collateral 
                    or keep it deposited for future credit access.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Collateral becomes withdrawable</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">No early repayment penalties</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="text-gray-300">Interest stops accruing immediately</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Legal Notice */}
        <div className="mt-8 p-4 bg-[#0d1d35] rounded-lg border border-[#1a2d4a]">
          <p className="text-xs text-gray-500 text-center">
            <strong className="text-gray-400">Risk Disclosure:</strong> Borrowing against collateral carries risk. 
            If your health factor drops below 1.20, your position may be liquidated. 
            Always maintain adequate collateral to avoid liquidation.
          </p>
        </div>
      </main>
    </div>
  );
};

export default DeFi;
